<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount } from "vue"
import {
  computeZoomRect,
  finalizeZoomClose,
  hideZoomSource,
  useZoom,
} from "@/composables/use-zoom"
import {
  PageEvent,
  useStudentPageTransitions,
} from "@/composables/use-student-page-transitions"

const { zoomSourceRect, zoomSourceElement, zoomScrollY, zoomData } = useZoom()
const { send, flightDirection } = useStudentPageTransitions()

const container = ref<HTMLDivElement>()

const flightRatio = () => {
  const d = zoomData.value
  if (d?.width && d?.height) return d.width / d.height
  const src = zoomSourceRect.value
  return src ? src.width / src.height : 1
}

const computeFlip = (src: DOMRect) => {
  const vpW = window.innerWidth
  const vpH = window.innerHeight
  const last = computeZoomRect(flightRatio())
  const scale = src.width / last.width
  const dx = src.left + src.width / 2 - vpW / 2
  const dy = src.top - -zoomScrollY.value + src.height / 2 - vpH / 2
  return { scale, dx, dy }
}

onMounted(() => {
  const direction = flightDirection.value

  const complete = () => {
    send(PageEvent.FLIGHT_COMPLETE)
  }

  if (!zoomSourceRect.value || !container.value) {
    complete()
    return
  }

  if (zoomSourceElement.value) {
    // Hide every on-page copy of the source for the whole flight (see
    // hideZoomSource). Going in: defer one tick so the clone is placed first and
    // there's no empty frame. Going out: hide immediately to re-assert against
    // any node Swiper relocated/duplicated while the zoom was open.
    if (direction === "in") {
      setTimeout(() => hideZoomSource(), 1)
    } else {
      zoomSourceElement.value.style.opacity = "0"
      hideZoomSource()
    }

    const clone = zoomSourceElement.value.cloneNode(true) as HTMLImageElement
    clone.removeAttribute("style")
    // Drop the source tag so hideZoomSource() can't match (and hide) the clone.
    clone.removeAttribute("data-zoom-id")
    clone.className = "object-contain select-none border"
    const last = computeZoomRect(flightRatio())
    clone.style.width = `${last.width}px`
    clone.style.height = `${last.height}px`
    clone.draggable = false
    container.value.appendChild(clone)
  }

  const { scale, dx, dy } = computeFlip(zoomSourceRect.value)
  const flip = `translate(${dx}px, ${dy}px) scale(${scale})`

  // "in":  start at the thumbnail (flip) → animate to centered/zoomed ("")
  // "out": start centered/zoomed ("") → animate back onto the thumbnail (flip)
  container.value.style.transform = direction === "out" ? "" : flip
  void container.value.getBoundingClientRect()
  container.value.style.transition = "transform 300ms ease-in-out"
  container.value.style.transform = direction === "out" ? flip : ""

  container.value.addEventListener("transitionend", complete, { once: true })
})

onBeforeUnmount(() => {
  // Restore all tagged copies of the source image now that the clone is gone.
  // This runs after the PANEL_FADE_MS linger (direction="out") so zoomData is
  // still populated and every Swiper duplicate is caught by querySelectorAll.
  if (flightDirection.value === "out") finalizeZoomClose()
})
</script>

<template>
  <div
    class="fixed inset-0 z-30 flex items-center justify-center pointer-events-none"
  >
    <div
      ref="container"
      class="absolute w-full h-full flex items-center justify-center"
    />
  </div>
</template>
